import { deburr, dropRight, each, filter, find, findIndex, get, isArray, keys, map, merge, orderBy, slice } from 'lodash';
import { defineStore } from 'pinia';
import ApiHelper from 'plugins/api';
import { tr } from 'plugins/i18n';
import router from 'plugins/router';
import { pinia } from 'plugins/store/index';
import { computed, ref } from 'vue';

type API_ITEM = {
  url?: string,
  loaded: boolean
}

interface Newstore extends Obj {
  deepLinked: boolean,
  page?: PageNews
  items: NewsItem[]
  apis: {
    page: API_ITEM
    list: API_ITEM
    details: API_ITEM
  },
  pagination: {
    page: number,
    perPage: number,
    max: number,
  },
  options: {
    category: {
      label: string,
      value: string,
    }[],
    ordering: {
      label: string,
      value: string,
      field: NewsItemOrder,
      order: 'asc' | 'desc',
    }[],
  }
  filters: {
    category?: string,
    ordering?: string,
  },
}
type FiltersKey = keyof Newstore["options"];


const useStore = defineStore('News', () => {
  const state = ref<Newstore>({
    loaded: false,
    deepLinked: true,
    apis: {
      page: {
        url: undefined,
        loaded: false,
      },
      list: {
        url: 'news',
        loaded: false,
      },
      details: {
        url: undefined,
        loaded: false,
      }
    },
    page: undefined,
    items: [],
    pagination: {
      page: 1,
      perPage: 12,
      max: 1,
    },
    options: {
      // add the field that we need filtering here
      category: [],
      // for ordering purpose only -> custom behavior on this.
      ordering: [
        {
          label: tr('PAGE_NEWS.filters.ordering.titleasc'),
          value: 'titleasc',
          field: 'title',
          order: 'asc',
        },
        {
          label: tr('PAGE_NEWS.filters.ordering.titledesc'),
          value: 'titledesc',
          field: 'title',
          order: 'desc',
        },
        {
          label: tr('PAGE_NEWS.filters.ordering.dateasc'),
          value: 'dateasc',
          field: 'dates.posted',
          order: 'asc',
        },
        {
          label: tr('PAGE_NEWS.filters.ordering.datedesc'),
          value: 'datedesc',
          field: 'dates.posted',
          order: 'desc',
        }
      ],
    },
    filters: {
      category: undefined,
      ordering: undefined,
    },
  })

  const setPage = (page: PageNews) => {
    console.debug('[news] setPage', page);
    state.value.page = page
    return page
  }

  const setItems = (items: NewsItem[]) => {
    // ensure if item before
    const previous = [...state.value.items]

    state.value.items = items

    if (previous.length > 0) {
      each(previous, mergeItem)
    }

    createFilters();

    state.value.pagination.max = Math.ceil(items.length / state.value.pagination.perPage)
    return state.value.items
  }

  const mergeItem = (item: NewsItem) => {
    console.debug('[news] mergeIten', { item });
    if (!item) return
    if (state.value.items.length === 0) {
      setItems([item])
      return item
    }
    const findex = findIndex(state.value.items, ({ id }) => id === item.id)
    if (findex < 0) return
    state.value.items[findex] = merge({}, state.value.items[findex], item)
    return state.value.items[findex]
  }

  const loadPage = async (url: string) => {
    const api = state.value.apis.page

    const request = api.loaded
      ? Promise.resolve(state.value.page)
      : ApiHelper.get(url, {})
        .then(({ data }: any) => {
          console.debug('[] ', { ...data });
          return data as PageNews
        })
        .then(setPage)
        .then(data => {
          api.loaded = true
          return data
        })
    return request
  }

  const loadList = async () => {
    const api = state.value.apis.list

    const request = api.loaded
      ? Promise.resolve(state.value.items)
      : ApiHelper.get(api?.url || '', api)
        .then(({ data }: any) => {
          return map(data, ({ ...item }: any) => ({
            ...item,
          }))
        })
        .then(setItems)
        .then(data => {
          api.loaded = true
          return data
        })

    return request
      .then(data => {
        return data
      })
  }

  const loadDetails = async (url: string, slug: string) => {
    const entry = getItemBySlug(slug)
    // if exist AND loaded+
    // if (entry?.loaded) return entry
    if (entry && ({}).hasOwnProperty.call(entry, 'loaded') && entry.loaded) {
      console.debug('[news] loadDetails - found loaded page', { entry });
      return Promise.resolve(entry)
    }

    const api = { ...state.value.apis.details }
    api.url = url

    return ApiHelper.get(api.url, api)
      .then(({ data }: any) => data as NewsItem)
      .then((data) => {
        console.debug('[news] loadDetail', data);

        return {
          ...data,
          loaded: true,
        }
      })
      .then(mergeItem)
  }

  const createFilters = () => {
    const filtersKeys = dropRight(keys(state.value.options));
    const rawItems = state.value.items;
    const rawData: any = {};

    each(filtersKeys, (key: string) => {
      rawData[key] = [];
    });

    each(rawItems, (item: any) => {
      each(filtersKeys, (key: string) => {
        if (item && item[key] && findIndex(rawData[key], { 'id': item[key].id }) < 0) {
          rawData[key].push(item[key]);
        }
      });
    });

    each(rawData, (filter, key) => {
      rawData[key] = map(filter, (i) => {
        return {
          label: i.title,
          value: i.slug,
        };
      })
    })

    merge(state.value.options, rawData);

    if (state.value.deepLinked) {
      setDefaultFilters();
    }

    return state.value.options;
  }

  const setDefaultFilters = () => {
    const routeQuery = router.currentRoute.value.query

    if (routeQuery.page) {
      state.value.pagination.page = parseInt(routeQuery.page as string, 10) || 1;
    }
    delete routeQuery.page;

    each(routeQuery, (value: any, key: any) => {
      state.value.filters[key as FiltersKey] = value;
    })
  }

  const syncRouterQuery = () => {
    const query: any = {};
    each(state.value.filters, (value: any, key: any) => {
      if (value && key) {
        query[key] = value;
      }
    });

    if (state.value.pagination.page) {
      query.page = state.value.pagination.page;
    }
    router.replace({ query: query })
  }

  const setPagination = (page: number) => {
    state.value.pagination.page = page;
  }

  const prevPagination = () => {
    if (state.value.pagination.page - 1 === 0) {
      state.value.pagination.page = 1
    } else {
      state.value.pagination.page = state.value.pagination.page - 1;
    }
  }

  const nextPagination = () => {
    if (state.value.pagination.page + 1 > state.value.pagination.max) {
      state.value.pagination.page = state.value.pagination.max
    } else {
      state.value.pagination.page = state.value.pagination.page + 1;
    }
  }

  // const getItemById = (id: number) => {
  //   return find(state.value.items, (item) => item.id === id)
  // }

  const getItemBySlug = (slug: string) => {
    return find(state.value.items, (item) => item.slug === slug)
  }

  // setFilter
  const setFilter = (key: FiltersKey, value?: string) => {
    // on filter change reset to page #1
    setPagination(1);

    state.value.filters[key] = value || undefined;

    return state.value.filters;
  }

  // Filtering here.
  const filtered = computed(() => {
    let rawItems = state.value.items;

    // feel free to add any other filtering options, like date range or year
    each(state.value.filters, (value?: string, key?: string) => {
      if (key && key !== 'ordering' && value) {
        rawItems = filter(rawItems, (item) => {
          const itemData = get(item, key);
          // if value is a string
          if (typeof itemData === 'string') {
            return itemData == value ? true : false
            // if value is a Category
          } else if (itemData?.slug) {
            return itemData.slug == value ? true : false
            // when there is multiple category (inside array)
          } else if (isArray(itemData)) {
            return find(itemData, { 'slug': value }) ? true : false
          }
          return false;
        })
      }
    })

    if (state.value.filters.ordering) {
      const orderOption = find(state.value.options.ordering, ['value', state.value.filters.ordering])
      if (orderOption) {
        rawItems = orderBy(rawItems, (item: any) => {
          return deburr(get(item, orderOption.field)?.toString() || '')
        }
          , orderOption.order);
      }
    }

    return rawItems
  });

  // const cards = computed(() => state.value.items)
  const paginated = computed(() => {
    const items = filtered.value;
    const { page, perPage } = state.value.pagination;
    const pageStart = (page - 1) * perPage;
    const pageEnd = pageStart + perPage;
    const paginated = slice(items, pageStart, pageEnd);

    // recompute max page from current filtered items
    state.value.pagination.max = Math.ceil(items.length / state.value.pagination.perPage)

    if (state.value.deepLinked) {
      syncRouterQuery()
    }

    return paginated;
  })

  // const getRelated = async (id?:number, max = 2) => {
  //   // if (!state.value.items?.length) {
  //   return loadList().then(() => {
  //     const items = state.value.items;
  //     const result = id ? sampleSize(reject(items, ['id', id]), max) : sampleSize(items, max);
  //     return result as NewsItem[];
  //   });
  //   // }

  //   // const items = state.value.items;
  //   // const result = id ? sampleSize(reject(items, ['id', id]), max) : sampleSize(items, max);
  //   // return result as NewsItem[];
  // }

  return {
    state,

    // LOAD
    loadPage,
    loadList,
    loadDetails,

    // pagination actions
    setPagination,
    prevPagination,
    nextPagination,

    // get by id
    // getItemById,

    // get by slug
    getItemBySlug,

    // // get related
    // getRelated,

    // get filters options
    options: computed(() => state.value.options),
    setFilter,
    // active filters
    filters: computed(() => state.value.filters),

    // raw data...
    // items numbers
    itemsNumber: computed(() => filtered.value.length),

    // filtered data without pagination..
    filtered,

    // real data.
    paginated,
    pagination: computed(() => state.value.pagination)
  }
})
export const store = useStore(pinia);
export default store;
